import React, { useEffect, useState, useRef, Component } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ShoppingCartIcon, MenuIcon, XIcon, UserIcon, LogOutIcon, LayoutDashboardIcon, PackageIcon, ChevronRightIcon, ShoppingBagIcon, TrashIcon, AlertTriangleIcon, DollarSignIcon, EyeIcon, SettingsIcon, SearchIcon, HelpCircleIcon, MailIcon, MessageSquareIcon } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import { AuthModal } from './auth/AuthModal';
import { ThemeToggle } from './ThemeToggle';
import { DeleteAccountModal } from './DeleteAccountModal';
import { useCart } from './cart/CartContext';
interface NavbarProps {
  scrolled: boolean;
}
export const Navbar: React.FC<NavbarProps> = ({
  scrolled
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [lastScrollY, setLastScrollY] = useState(0);
  const [show, setShow] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup'>('signin');
  const [redirectTo, setRedirectTo] = useState<string | undefined>(undefined);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    user,
    logout,
    switchRole
  } = useAuth();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const {
    totalItems,
    toggleCart
  } = useCart();
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [contactMenuOpen, setContactMenuOpen] = useState(false);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  const contactMenuRef = useRef<HTMLDivElement>(null);
  const contactButtonRef = useRef<HTMLButtonElement>(null);
  // Add click outside handler for profile menu
  useEffect(() => {
    // Using a more robust approach for handling outside clicks
    const handleOutsideClick = (event: MouseEvent) => {
      // Don't do anything if the menu isn't open
      if (!profileMenuOpen) return;
      // Check if click is outside both the button and the menu
      const clickedElement = event.target as Node;
      const isOutsideMenu = profileMenuRef.current && !profileMenuRef.current.contains(clickedElement);
      const isOutsideButton = profileButtonRef.current && !profileButtonRef.current.contains(clickedElement);
      if (isOutsideMenu && isOutsideButton) {
        // Close the menu on the next tick to avoid race conditions
        setTimeout(() => setProfileMenuOpen(false), 0);
      }
    };
    // Add the event listener to the capturing phase (true as third parameter)
    // This ensures our handler runs before other handlers
    document.addEventListener('click', handleOutsideClick, true);
    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [profileMenuOpen]);
  // Add click outside handler for help menu
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!helpMenuOpen) return;
      const clickedElement = event.target as Node;
      const isOutsideMenu = helpMenuRef.current && !helpMenuRef.current.contains(clickedElement);
      const isOutsideButton = helpButtonRef.current && !helpButtonRef.current.contains(clickedElement);
      if (isOutsideMenu && isOutsideButton) {
        setTimeout(() => setHelpMenuOpen(false), 0);
      }
    };
    document.addEventListener('click', handleOutsideClick, true);
    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [helpMenuOpen]);
  // Add click outside handler for contact menu
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!contactMenuOpen) return;
      const clickedElement = event.target as Node;
      const isOutsideMenu = contactMenuRef.current && !contactMenuRef.current.contains(clickedElement);
      const isOutsideButton = contactButtonRef.current && !contactButtonRef.current.contains(clickedElement);
      if (isOutsideMenu && isOutsideButton) {
        setTimeout(() => setContactMenuOpen(false), 0);
      }
    };
    document.addEventListener('click', handleOutsideClick, true);
    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [contactMenuOpen]);
  // Handle profile menu toggle
  const handleProfileMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up
    setProfileMenuOpen(!profileMenuOpen);
  };
  // Handle delete account
  const handleDeleteAccount = () => {
    setProfileMenuOpen(false);
    setMobileMenuOpen(false);
    setDeleteAccountModalOpen(true);
  };
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Hide/show navbar based on scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShow(false);
      } else {
        setShow(true);
      }
      setLastScrollY(currentScrollY);
      // Update active section
      if (location.pathname === '/') {
        const sections = ['hero', 'products', 'pricing', 'learn'];
        const scrollPosition = window.scrollY + 100;
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetBottom = offsetTop + element.offsetHeight;
            if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, location.pathname]);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };
  const handleLogout = async () => {
    try {
      await logout();
      // Fallback if page reload in AuthProvider fails
      setProfileMenuOpen(false);
      setMobileMenuOpen(false);
      window.location.href = '/';
    } catch (error) {
      console.error('Error during logout:', error);
      alert('Failed to log out. Refreshing the page to reset application state.');
      // Close menus even on error
      setProfileMenuOpen(false);
      setMobileMenuOpen(false);
      window.location.href = '/';
    }
  };
  const openAuthModal = (mode: 'signin' | 'signup', redirect?: string) => {
    setAuthModalMode(mode);
    setRedirectTo(redirect);
    setAuthModalOpen(true);
    setMobileMenuOpen(false);
  };
  const handleLogoClick = () => {
    if (isSellerMode()) {
      navigate('/seller-dashboard');
    } else if (isAuthenticated) {
      navigate('/buyer-dashboard');
    } else {
      navigate('/');
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setMobileMenuOpen(false);
  };
  const navigateToDashboard = () => {
    // Route based on active role
    if (isSellerMode()) {
      navigate('/seller-dashboard');
    } else {
      // Navigate to buyer dashboard
      navigate('/buyer-dashboard');
    }
    setProfileMenuOpen(false);
    setMobileMenuOpen(false);
  };
  // Function to handle Browse link click
  const handleBrowseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Simply navigate to browse page without changing any other state
    navigate('/browse');
    setMobileMenuOpen(false);
  };
  // Function to scroll to the learn section without redirecting
  const scrollToLearnSection = () => {
    const learnSection = document.getElementById('resources');
    if (learnSection) {
      learnSection.scrollIntoView({
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };
  // Function to scroll to the resources section, navigating home first if needed
  const handleResourcesClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setMobileMenuOpen(false);
      setTimeout(() => {
        const resourcesSection = document.getElementById('resources');
        if (resourcesSection) {
          resourcesSection.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const resourcesSection = document.getElementById('resources');
      if (resourcesSection) {
        resourcesSection.scrollIntoView({
          behavior: 'smooth'
        });
      }
      setMobileMenuOpen(false);
    }
  };
  // Determine navbar background style based on scroll position
  const getNavbarStyle = () => {
    if (scrolled) {
      return 'bg-white dark:bg-[#162825] shadow-sm border-b border-gray-100 dark:border-[#1A3330] py-3';
    } else {
      return 'bg-[#F8FAFA] dark:bg-[#162825]/95 border-b border-[#E1E5E4] dark:border-[#1A3330] backdrop-blur-md py-4';
    }
  };
  // Check if current path is dashboard
  const isDashboardActive = () => {
    return location.pathname.includes('dashboard');
  };
  // Check if user is in seller mode
  const isSellerMode = () => {
    // Only return true if we're fully authenticated and the role is definitely 'seller'
    return Boolean(isAuthenticated && user && user.activeRole === 'seller');
  };
  // Check if current path matches a route
  const isRouteActive = (route: string) => {
    if (route === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(route);
  };
  // Toggle search input on mobile
  const toggleSearchInput = () => {
    setShowSearchInput(!showSearchInput);
    // Focus the input when showing
    if (!showSearchInput) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };
  // Close search on ESC key
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSearchInput) {
        setShowSearchInput(false);
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showSearchInput]);
  // Check if current page is the landing page
  const isLandingPage = location.pathname === '/';
  // Check if current page is a browsing/marketplace page (for mobile search visibility)
  const isBrowsingPage = location.pathname === '/browse' || location.pathname.startsWith('/browse/') || location.pathname.startsWith('/products') || location.pathname.startsWith('/product/');
  // Add handleSearchSubmit function
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement search functionality
    if (searchTerm.trim()) {
      // Navigate to search results page with query parameter
      navigate(`/browse?search=${encodeURIComponent(searchTerm.trim())}`);
      // Close mobile search if open
      setShowSearchInput(false);
      // Clear search term after submission
      setSearchTerm('');
    }
  };
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${getNavbarStyle()} ${show ? 'transform translate-y-0' : 'transform -translate-y-full'}`} data-id="element-1213">
        <div className="container mx-auto px-6 md:px-10 flex items-center justify-between" data-id="element-1214">
          {/* Logo */}
          <div className="flex items-center" data-id="element-1215">
            <button onClick={handleLogoClick} className="text-2xl font-bold inline-block" data-id="element-1216">
              <span className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-1217">
                DG
              </span>
              <span className="bg-gradient-to-r from-[#2DB489] to-[#3DC79D] dark:from-[#4ED1A0] dark:to-[#6FDEBB] text-transparent bg-clip-text" data-id="element-1218">
                Stack
              </span>
            </button>
          </div>

          {/* Nav Links - Desktop */}
          <nav className="hidden md:flex items-center space-x-8" data-id="element-1219">
            {!isSellerMode() ?
          // Buyer mode navigation
          <>
                <button onClick={handleBrowseClick} className={`text-sm font-medium relative group ${isRouteActive('/browse') ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-slate-500 dark:text-[#B0C1BD]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1220">
                  Browse
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out ${isRouteActive('/browse') ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-1221"></span>
                </button>
                {!isAuthenticated && <button onClick={() => navigate('/sell-with-us')} className={`text-sm font-medium relative group ${isRouteActive('/sell-with-us') ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-slate-500 dark:text-[#B0C1BD]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1222">
                    Sell
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out ${isRouteActive('/sell-with-us') ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-1223"></span>
                  </button>}
                <button onClick={handleResourcesClick} className="text-sm font-medium relative group text-slate-500 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" data-id="element-1224">
                  Resources
                  <span className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out w-0 group-hover:w-full" data-id="element-1225"></span>
                </button>
                <Link to="/contact" className={`text-sm font-medium relative group ${isRouteActive('/contact') ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-slate-500 dark:text-[#B0C1BD]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1226">
                  Contact
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out ${isRouteActive('/contact') ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-1227"></span>
                </Link>
                {isAuthenticated && <button onClick={navigateToDashboard} className={`text-sm font-medium relative group ${isDashboardActive() ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-slate-500 dark:text-[#B0C1BD]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1228">
                    Dashboard
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out ${isDashboardActive() ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-1229"></span>
                  </button>}
              </> :
          // Seller mode navigation - add Contact dropdown
          <>
                <button onClick={handleBrowseClick} className={`text-sm font-medium relative group ${location.pathname === '/browse' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-slate-500 dark:text-[#B0C1BD]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1230">
                  Browse
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out ${location.pathname === '/browse' ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-1231"></span>
                </button>

                {/* Contact dropdown menu for seller mode */}
                <div className="relative" ref={contactMenuRef} data-id="element-1232">
                  <button ref={contactButtonRef} onClick={() => setContactMenuOpen(!contactMenuOpen)} className={`text-sm font-medium relative group flex items-center text-slate-500 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1233">
                    Contact
                    <ChevronDownIcon className="ml-1 h-4 w-4 transition-transform duration-200" style={{
                  transform: contactMenuOpen ? 'rotate(180deg)' : 'rotate(0)'
                }} data-id="element-1234" />
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out w-0 group-hover:w-full`} data-id="element-1235"></span>
                  </button>
                  {contactMenuOpen && <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#162825] rounded-lg shadow-lg py-2 z-50 border border-gray-100 dark:border-[#1A3330] transform origin-top-right transition-all duration-200" data-id="element-1236">
                      <Link to="/support/email" className="flex items-center px-4 py-2 text-sm text-slate-500 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" onClick={() => setContactMenuOpen(false)} data-id="element-1237">
                        <MailIcon className="h-4 w-4 mr-2" data-id="element-1238" />
                        Email Support
                      </Link>
                      <Link to="/support/chat" className="flex items-center px-4 py-2 text-sm text-slate-500 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" onClick={() => setContactMenuOpen(false)} data-id="element-1239">
                        <MessageSquareIcon className="h-4 w-4 mr-2" data-id="element-1240" />
                        Live Chat
                      </Link>
                    </div>}
                </div>
                {location.pathname === '/' && <button onClick={scrollToLearnSection} className={`text-sm font-medium relative group text-slate-500 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1241">
                    Learn
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out w-0 group-hover:w-full`} data-id="element-1242"></span>
                  </button>}
                <button onClick={navigateToDashboard} className={`text-sm font-medium relative group ${isDashboardActive() ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-slate-500 dark:text-[#B0C1BD]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1243">
                  Dashboard
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300 ease-in-out ${isDashboardActive() ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-1244"></span>
                </button>
              </>}
          </nav>

          {/* Nav Actions - Desktop */}
          <div className="hidden md:flex items-center space-x-6" data-id="element-1245">
            {/* Search input - Desktop (only for signed-in users) */}
            {isAuthenticated && <form onSubmit={handleSearchSubmit} className="relative" data-id="element-1246">
                <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-40 py-1.5 px-3 pr-8 text-sm rounded-md border border-gray-200 dark:border-[#1A3330] bg-white dark:bg-[#162825] text-gray-800 dark:text-[#F8FAF9] focus:outline-none focus:ring-1 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] transition-all duration-200" data-id="element-1247" />
                <button type="submit" className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-1248">
                  <SearchIcon className="h-4 w-4" data-id="element-1249" />
                </button>
              </form>}
            {/* Theme Toggle */}
            <ThemeToggle data-id="element-1250" />
            {isAuthenticated ? <div className="relative" ref={profileMenuRef} data-id="element-1251">
                <button ref={profileButtonRef} onClick={handleProfileMenuToggle} className="flex items-center space-x-2 focus:outline-none group" data-id="element-1252">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white overflow-hidden transition-transform duration-300 group-hover:scale-110" data-id="element-1253">
                    <UserIcon className="h-4 w-4" data-id="element-1254" />
                  </div>
                </button>
                {profileMenuOpen && <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#162825] rounded-lg shadow-lg py-2 z-50 border border-gray-100 dark:border-[#1A3330] transform origin-top-right transition-all duration-200" onClick={e => {
              // Prevent all clicks inside the menu from bubbling up
              e.stopPropagation();
            }} data-id="element-1255">
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-[#1A3330] mb-1" data-id="element-1256">
                      <p className="text-sm font-medium text-[#0F3B46] dark:text-[#F8FAF9]" data-id="element-1257">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-[#7A8F8A] truncate" data-id="element-1258">
                        {user?.email}
                      </p>
                    </div>
                    {/* Settings link */}
                    <Link to="/account-settings/profile" className="flex items-center px-4 py-2 text-sm text-slate-500 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" onClick={e => {
                // Stop the event from propagating
                e.stopPropagation();
                // Close the menu
                setProfileMenuOpen(false);
              }} data-id="element-1259">
                      <SettingsIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1260" />
                      Account Settings
                    </Link>
                    {/* Role switch button */}
                    <button onClick={e => {
                e.stopPropagation();
                const newRole = user?.activeRole === 'seller' ? 'buyer' : 'seller';
                switchRole(newRole).then(() => {
                  setProfileMenuOpen(false);
                  navigate(newRole === 'seller' ? '/seller-dashboard' : '/buyer-dashboard');
                }).catch(() => {});
              }} className="flex items-center w-full text-left px-4 py-2 text-sm text-slate-500 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" data-id="element-1261">
                      <PackageIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1262" />
                      {user?.activeRole === 'seller' ? 'Switch to Buyer View' : 'Switch to Seller View'}
                    </button>
                    <hr className="my-1 border-gray-100 dark:border-[#1A3330]" data-id="element-1263" />
                    {/* Logout button */}
                    <button onClick={e => {
                // Stop the event from propagating
                e.stopPropagation();
                // Close the menu first
                setProfileMenuOpen(false);
                // Then handle logout
                handleLogout();
              }} className="flex items-center w-full text-left px-4 py-2 text-sm text-slate-500 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" data-id="element-1264">
                      <LogOutIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1265" />
                      Log Out
                    </button>
                  </div>}
              </div> : <button onClick={() => openAuthModal('signin')} className="text-sm font-medium text-slate-500 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200 flex items-center" data-id="element-1266">
                <UserIcon className="h-4 w-4 mr-1.5" data-id="element-1267" />
                Sign In
              </button>}
            {isAuthenticated ? null : <button onClick={() => openAuthModal('signup')} className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:shadow-lg transition-all duration-300 hover:scale-103 relative overflow-hidden group dark-glow" data-id="element-1268">
                <span className="relative z-10" data-id="element-1269">Sign Up</span>
                <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" data-id="element-1270"></span>
                <span className="absolute -inset-x-1 bottom-0 h-[2px] bg-white/20" data-id="element-1271"></span>
              </button>}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4" data-id="element-1272">
            {/* Search icon - Mobile (only on browsing/marketplace pages) */}
            {isAuthenticated && isBrowsingPage && <button onClick={toggleSearchInput} className="text-slate-500 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" aria-label="Search" data-id="element-1273">
                <SearchIcon className="h-5 w-5" data-id="element-1274" />
              </button>}
            {/* Cart Icon - Only show for authenticated users and not on landing page */}
            {isAuthenticated && !isLandingPage && <button onClick={toggleCart} className="relative p-1.5 text-slate-500 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" aria-label="Shopping Cart" data-id="element-1275">
                <ShoppingCartIcon className="h-5 w-5" data-id="element-1276" />
                {totalItems > 0 && <span className="absolute -top-1 -right-1 bg-[#2DB489] dark:bg-[#4ED1A0] text-white text-xs w-4 h-4 flex items-center justify-center rounded-full" data-id="element-1277">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>}
              </button>}
            <ThemeToggle data-id="element-1278" />
            {isAuthenticated && <div className="relative" ref={profileMenuRef} data-id="element-1279">
                <button ref={profileButtonRef} onClick={handleProfileMenuToggle} className="flex items-center space-x-2 focus:outline-none group" data-id="element-1280">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white overflow-hidden transition-transform duration-300 group-hover:scale-110" data-id="element-1281">
                    <UserIcon className="h-4 w-4" data-id="element-1282" />
                  </div>
                </button>
                {profileMenuOpen && <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#162825] rounded-lg shadow-lg py-2 z-50 border border-gray-100 dark:border-[#1A3330] transform origin-top-right transition-all duration-200" onClick={e => {
              // Prevent all clicks inside the menu from bubbling up
              e.stopPropagation();
            }} data-id="element-1283">
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-[#1A3330] mb-1" data-id="element-1284">
                      <p className="text-sm font-medium text-[#0F3B46] dark:text-[#F8FAF9]" data-id="element-1285">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-[#7A8F8A] truncate" data-id="element-1286">
                        {user?.email}
                      </p>
                    </div>
                    {/* Settings link */}
                    <Link to="/account-settings/profile" className="flex items-center px-4 py-2 text-sm text-slate-500 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" onClick={e => {
                // Stop the event from propagating
                e.stopPropagation();
                // Close the menu
                setProfileMenuOpen(false);
              }} data-id="element-1287">
                      <SettingsIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1288" />
                      Account Settings
                    </Link>
                    {/* Role switch button - mobile */}
                    <button onClick={e => {
                e.stopPropagation();
                const newRole = user?.activeRole === 'seller' ? 'buyer' : 'seller';
                switchRole(newRole).then(() => {
                  setProfileMenuOpen(false);
                  navigate(newRole === 'seller' ? '/seller-dashboard' : '/buyer-dashboard');
                }).catch(() => {});
              }} className="flex items-center w-full text-left px-4 py-2 text-sm text-slate-500 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" data-id="element-1289">
                      <PackageIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1290" />
                      {user?.activeRole === 'seller' ? 'Switch to Buyer View' : 'Switch to Seller View'}
                    </button>
                    <hr className="my-1 border-gray-100 dark:border-[#1A3330]" data-id="element-1291" />
                    {/* Logout button */}
                    <button onClick={e => {
                // Stop the event from propagating
                e.stopPropagation();
                // Close the menu first
                setProfileMenuOpen(false);
                // Then handle logout
                handleLogout();
              }} className="flex items-center w-full text-left px-4 py-2 text-sm text-slate-500 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" data-id="element-1292">
                      <LogOutIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1293" />
                      Log Out
                    </button>
                  </div>}
              </div>}
            <button className="text-slate-500 dark:text-[#B0C1BD] focus:outline-none" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-1294">
              {mobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-1295" /> : <MenuIcon className="h-6 w-6" data-id="element-1296" />}
            </button>
          </div>
        </div>

        {/* Mobile Search Input (when active, only on browsing/marketplace pages) */}
        {isAuthenticated && isBrowsingPage && showSearchInput && <div className="md:hidden bg-white dark:bg-[#162825] px-6 py-3 border-t border-gray-100 dark:border-[#1A3330]" data-id="element-1297">
            <form onSubmit={handleSearchSubmit} className="relative" data-id="element-1298">
              <input ref={searchInputRef} type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full py-2 px-3 pr-10 text-sm rounded-md border border-gray-200 dark:border-[#1A3330] bg-white dark:bg-[#162825] text-gray-800 dark:text-[#F8FAF9] focus:outline-none focus:ring-1 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" data-id="element-1299" />
              <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-1300">
                <SearchIcon className="h-5 w-5" data-id="element-1301" />
              </button>
              <button type="button" onClick={() => setShowSearchInput(false)} className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-1302">
                <XIcon className="h-5 w-5" data-id="element-1303" />
              </button>
            </form>
          </div>}
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="fixed inset-0 z-40 bg-white dark:bg-[#162825] pt-20 pb-6 px-6 md:hidden overflow-y-auto" data-id="element-1304">
          <nav className="flex flex-col space-y-6 py-8" data-id="element-1305">
            {/* Mobile navigation items */}
            <button onClick={handleBrowseClick} className={`flex items-center justify-between text-lg font-medium ${isRouteActive('/browse') ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-[#0F3B46] dark:text-[#F8FAF9]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1306">
              <span data-id="element-1307">Browse</span>
              <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1308" />
            </button>
            {!isAuthenticated && <button onClick={() => {
          navigate('/sell-with-us');
          setMobileMenuOpen(false);
        }} className={`flex items-center justify-between text-lg font-medium ${isRouteActive('/sell-with-us') ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-[#0F3B46] dark:text-[#F8FAF9]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1309">
                Sell
                <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1310" />
              </button>}
            <button onClick={handleResourcesClick} className="flex items-center justify-between text-lg font-medium text-[#0F3B46] dark:text-[#F8FAF9] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" data-id="element-1311">
              Resources
              <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1312" />
            </button>

            <Link to="/contact" className={`flex items-center justify-between text-lg font-medium ${isRouteActive('/contact') ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-[#0F3B46] dark:text-[#F8FAF9]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1313">
              Contact
              <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1314" />
            </Link>

            {isSellerMode() && <button onClick={() => {
          if (location.pathname !== '/') {
            navigate('/');
            setMobileMenuOpen(false);
            setTimeout(() => {
              const learnSection = document.getElementById('resources');
              if (learnSection) {
                learnSection.scrollIntoView({
                  behavior: 'smooth'
                });
              }
            }, 100);
          } else {
            scrollToLearnSection();
          }
        }} className={`flex items-center justify-between text-lg font-medium text-[#0F3B46] dark:text-[#F8FAF9] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1315">
                Learn
                <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1316" />
              </button>}
            {/* Dashboard link - only for authenticated users */}
            {isAuthenticated && <button onClick={navigateToDashboard} className={`flex items-center justify-between text-lg font-medium ${isDashboardActive() ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-[#0F3B46] dark:text-[#F8FAF9]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1317">
                Dashboard
                <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1318" />
              </button>}

            <div className="h-px bg-gray-100 dark:bg-[#1A3330] w-full my-2" data-id="element-1319"></div>

            {/* Auth actions */}
            {!isAuthenticated ? <>
                <button onClick={() => openAuthModal('signin')} className="flex items-center justify-between text-lg font-medium text-[#0F3B46] dark:text-[#F8FAF9] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200" data-id="element-1320">
                  <span className="flex items-center" data-id="element-1321">
                    <UserIcon className="h-5 w-5 mr-3 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1322" />
                    Sign In
                  </span>
                  <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1323" />
                </button>
                <button onClick={() => openAuthModal('signup')} className="flex items-center justify-center text-lg font-semibold bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-5 py-3 rounded-lg hover:shadow-lg transition-all duration-300" data-id="element-1324">
                  Sign Up
                </button>
              </> : <>
                <Link to="/account-settings/profile" onClick={() => setMobileMenuOpen(false)} className={`flex items-center justify-between text-lg font-medium ${isRouteActive('/account-settings') ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-[#0F3B46] dark:text-[#F8FAF9]'} hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors duration-200`} data-id="element-1325">
                  <span className="flex items-center" data-id="element-1326">
                    <SettingsIcon className="h-5 w-5 mr-3 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1327" />
                    Account Settings
                  </span>
                  <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1328" />
                </Link>
                <button onClick={() => {
            setMobileMenuOpen(false);
            handleLogout();
          }} className="flex items-center justify-between text-lg font-medium text-[#0F3B46] dark:text-[#F8FAF9] hover:text-red-500 dark:hover:text-red-400 transition-colors duration-200" data-id="element-1329">
                  <span className="flex items-center" data-id="element-1330">
                    <LogOutIcon className="h-5 w-5 mr-3 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1331" />
                    Log Out
                  </span>
                  <ChevronRightIcon className="h-5 w-5 text-slate-400 dark:text-[#7A8F8A]" data-id="element-1332" />
                </button>
              </>}
          </nav>
        </div>}

      {/* Auth Modal */}
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode={authModalMode} redirectTo={redirectTo} data-id="element-1333" />

      {/* Delete Account Modal */}
      {deleteAccountModalOpen && <DeleteAccountModal isOpen={deleteAccountModalOpen} onClose={() => setDeleteAccountModalOpen(false)} data-id="element-1334" />}
    </>;
};